import dayjs from 'dayjs';

import {ELeadStage, type ILead} from 'src/services/api/leads/types';
import {EMessageSystemMessageType, type IMessage} from 'src/services/api/messages/types';
import { EActionSuggestionType, type FinderCriteriaFn, type IActionSuggestion} from './types';
import {type IShowing} from 'src/api/landlord-showings-api';
import { LeadUtils } from 'src/utils/LeadUtils';

/**
 * The lead's most recent non-canceled tour whose scheduled time has already passed
 * and whose attendance is not yet recorded (`is_attended === null`). This is the tour
 * the "Tour Completed" nudge asks the user to mark attended or missed.
 */
const getPastUnmarkedTour = (lead: ILead, showings: IShowing[]): IShowing | undefined => {
    const now = dayjs();
    return showings
        .filter(o =>
            o.lead_uuid === lead.uuid &&
            !o.is_canceled &&
            o.is_attended === null &&
            dayjs(o.start_time).isBefore(now),
        )
        .toSorted((a, b) => dayjs(b.start_time).diff(dayjs(a.start_time)))[0];
};

/**
 * The lead's latest non-canceled tour, or undefined if they have none. Used to
 * drive attendance-outcome nudges off the most recent tour's status.
 */
const getLatestTour = (lead: ILead, showings: IShowing[]): IShowing | undefined =>
    showings
        .filter(o => o.lead_uuid === lead.uuid && !o.is_canceled)
        .toSorted((a, b) => dayjs(b.start_time).diff(dayjs(a.start_time)))[0];

/**
 * True when the lead's latest tour was marked missed and they have no upcoming
 * tour to replace it — the "Tour Missed" nudge then prompts rescheduling.
 */
const hasMissedTourToReschedule = (lead: ILead, showings: IShowing[]): boolean => {
    const latest = getLatestTour(lead, showings);
    const hasUpcoming = showings.some(
        o => o.lead_uuid === lead.uuid && !o.is_canceled && dayjs(o.start_time).isAfter(dayjs()),
    );
    return !hasUpcoming && latest?.is_attended === false;
};

// Truncated tighter than the app-wide default to keep the inline nudge message on one line for <14" screen
const NUDGE_LEAD_NAME_MAX_LENGTH = 18;

const getStopMessageNotification = (lead: ILead, messages: IMessage[]): IActionSuggestion | undefined => {
    // a lead already in Not Interested needs no nudge to move them there again
    if (lead.stage === ELeadStage.NOT_INTERESTED) {
        return;
    }

    const lastMessage = messages.filter(o => o.direction === 'in').toSorted((a, b) => dayjs(b.inserted_at).diff(dayjs(a.inserted_at)))[0];

    if (!lastMessage) {
        return;
    }

    const trimmedLower = lastMessage.contents.trim().toLowerCase();

    // e.g. "stop all", "please stop", "i am not interested", "no longer interested"
    const hasStopMessage = trimmedLower.includes('stop') && trimmedLower.length < 15;
    const hasNotInterestedMessage = trimmedLower.includes('not interested') || trimmedLower.includes('no longer interested') && trimmedLower.length < 30;

    if (hasStopMessage || hasNotInterestedMessage) {
        return {
            title: '🛑 Set to Not Interested',
            description: `${LeadUtils.getLeadNameWithEllipsis(lead.name)} may want to stop hearing from us. Consider setting them to Not Interested.`,
            type: EActionSuggestionType.warning,
            changeToSpecificStage: ELeadStage.NOT_INTERESTED
        };
    }
};

const getSystemMessageNotification = (lead: ILead, messages: IMessage[]): IActionSuggestion | undefined => {
    const lastMessage = messages.toSorted((a, b) => dayjs(b.inserted_at).diff(dayjs(a.inserted_at)))[0];

    let changeToSpecificStage: ELeadStage | undefined;
    let title: string | undefined;
    let description: string | undefined;

    switch (lastMessage?.system_message_type) {
        case EMessageSystemMessageType.APPLICATION_RECEIVED:
            changeToSpecificStage = ELeadStage.APPLICATION_COMPLETED;
            title = '📋 Application Received';
            description = `Review ${LeadUtils.getLeadNameWithEllipsis(lead.name)}'s completed application and update lead stage.`;
            break;
        case EMessageSystemMessageType.APPLICATION_REQUESTED:
            changeToSpecificStage = ELeadStage.INVITED_TO_APPLY;
            title = '📝 Application Requested';
            description = `${LeadUtils.getLeadNameWithEllipsis(lead.name)} has been invited to apply. Update lead stage to track application progress.`;
            break;
        case EMessageSystemMessageType.TOUR_CANCELLED:
            changeToSpecificStage = ELeadStage.TOUR_REQUESTED;
            title = '❌ Tour Cancelled';
            description = `Tour with ${LeadUtils.getLeadNameWithEllipsis(lead.name)} was cancelled. Update lead stage to reschedule.`;
            break;
        case EMessageSystemMessageType.TOUR_CREATED:
        case EMessageSystemMessageType.TOUR_UPDATED:
            changeToSpecificStage = ELeadStage.TOUR_SCHEDULED;
            title = '📅 Tour Scheduled';
            description = `Tour has been scheduled with ${LeadUtils.getLeadNameWithEllipsis(lead.name)}. Update lead stage to track progress.`;
            break;
        case EMessageSystemMessageType.TOUR_REQUESTED:
            changeToSpecificStage = ELeadStage.TOUR_REQUESTED;
            title = '🔔 Tour Requested';
            description = `${LeadUtils.getLeadNameWithEllipsis(lead.name)} has requested a tour. Update lead stage to coordinate scheduling.`;
            break;
        default:
            return undefined;
    }

    if (changeToSpecificStage === lead.stage) {
        return undefined;
    }

    return {
        title,
        description,
        type: EActionSuggestionType.default,
        changeToSpecificStage,
    };
};

const STAGE_TO_NOTIFICATION_MAP: Partial<Record<ELeadStage, FinderCriteriaFn>> = {
    [ELeadStage.NEW]: (lead) => {
        return {
            title: '👋 New Lead Ready',
            description: `Begin the conversation with ${LeadUtils.getLeadNameWithEllipsis(lead.name, NUDGE_LEAD_NAME_MAX_LENGTH)} and qualify their interest.`,
            type: EActionSuggestionType.default,
        };
    },
    [ELeadStage.CONTACTED]: (lead) => {
        return {
            title: '📱 Initial Contact Made',
            description: `Follow up with ${LeadUtils.getLeadNameWithEllipsis(lead.name)} to schedule a property tour.`,
            type: EActionSuggestionType.default,
        };
    },
    [ELeadStage.TOUR_REQUESTED]: (lead) => {
        return {
            title: '📅 Tour Request Pending',
            description: `Confirm tour details with ${LeadUtils.getLeadNameWithEllipsis(lead.name)} and set a specific time.`,
            type: EActionSuggestionType.default,
        };
    },
    [ELeadStage.TOUR_SCHEDULED]: (lead, messages, showings) => {
        const pastUnmarkedTour = getPastUnmarkedTour(lead, showings);
        if (pastUnmarkedTour) {
            return {
                title: '✅ Tour Completed',
                description: `${LeadUtils.getLeadNameWithEllipsis(lead.name)}'s scheduled tour time has already passed. Check with the lead and see if they attended.`,
                type: EActionSuggestionType.default,
                tourStatusAction: { tourId: pastUnmarkedTour.id },
            };
        }
        if (getLatestTour(lead, showings)?.is_attended === true) {
            return {
                title: '✅ Tour Attended',
                description: `${LeadUtils.getLeadNameWithEllipsis(lead.name)} attended the tour. Move them to Tour Completed and followup with next steps.`,
                type: EActionSuggestionType.default,
                changeToSpecificStage: ELeadStage.TOUR_COMPLETED,
                hideScheduleTour: true,
            };
        }
        if (hasMissedTourToReschedule(lead, showings)) {
            return {
                title: '🗓️ Tour Missed',
                description: `${LeadUtils.getLeadNameWithEllipsis(lead.name)} missed their tour. Check with the lead to schedule a tour at a different time.`,
                type: EActionSuggestionType.default,
            };
        }
        const hasUpcomingShowing = showings.some(o => o.lead_uuid === lead.uuid && dayjs(o.start_time).isAfter(dayjs()));
        if (!hasUpcomingShowing) {
            return {
                title: '⚠️ No Upcoming Tour',
                description: `${LeadUtils.getLeadNameWithEllipsis(lead.name)} has a scheduled tour but no upcoming showing. Please confirm the tour details.`,
                type: EActionSuggestionType.default,
                changeToSpecificStage: ELeadStage.TOUR_COMPLETED,
            };
        }
        return {
            title: '🕒 Tour Coming Up',
            description: `Prepare for ${LeadUtils.getLeadNameWithEllipsis(lead.name)}'s upcoming property tour.`,
            type: EActionSuggestionType.default,
        };
    },
    [ELeadStage.TOUR_COMPLETED]: (lead) => {
        return {
            title: '✨ Tour Completed',
            description: `Follow up with ${LeadUtils.getLeadNameWithEllipsis(lead.name)} about their tour experience and next steps.`,
            type: EActionSuggestionType.default,
        };
    },
    [ELeadStage.INVITED_TO_APPLY]: (lead) => {
        return {
            title: '📝 Application Pending',
            description: `Monitor ${LeadUtils.getLeadNameWithEllipsis(lead.name)}'s application progress and assist if needed.`,
            type: EActionSuggestionType.default,
        };
    },
    [ELeadStage.APPLICATION_COMPLETED]: (lead) => {
        return {
            title: '📋 Application Ready',
            description: `You can move ${LeadUtils.getLeadNameWithEllipsis(lead.name)} to Application Approved if their application looks good.`,
            type: EActionSuggestionType.default,
        };
    },
    [ELeadStage.APPLICATION_APPROVED]: (lead) => {
        return {
            title: '✅ Ready for Lease',
            description: `Send lease documents to ${LeadUtils.getLeadNameWithEllipsis(lead.name)} for signature.`,
            type: EActionSuggestionType.default,
        };
    },
    [ELeadStage.LEASE_SIGNED]: (lead) => {
        return {
            title: '🔑 Move-in Ready',
            description: `Coordinate move-in details with ${LeadUtils.getLeadNameWithEllipsis(lead.name)}.`,
            type: EActionSuggestionType.default,
        };
    },
    [ELeadStage.NOT_INTERESTED]: (lead) => {
        return {
            title: '📌 Lead Inactive',
            description: `${LeadUtils.getLeadNameWithEllipsis(lead.name)} marked as not interested. Consider for future properties.`,
            type: EActionSuggestionType.default,
        };
    },
    [ELeadStage.NOT_QUALIFIED]: (lead) => {
        return {
            title: '⚠️ Not Qualified',
            description: `${LeadUtils.getLeadNameWithEllipsis(lead.name)} does not meet the requirements for this property. Consider suggesting other properties that might be a better fit.`,
            type: EActionSuggestionType.default,
        };
    }
};


export const getActionSuggestionForLead = (lead: ILead, messages: IMessage[], showings: IShowing[]): IActionSuggestion | undefined => {
    const stopMessageNotification = getStopMessageNotification(lead, messages);
    if (stopMessageNotification) {
        return stopMessageNotification;
    }

    const systemMessageNotification = getSystemMessageNotification(lead, messages);

    if (systemMessageNotification) {
        return systemMessageNotification;
    }

    const fn = STAGE_TO_NOTIFICATION_MAP[lead.stage];
    if (!fn) {
        return;
    }
    return fn(lead, messages, showings);
};
